// Date helpers that work in the gym's time zone, independent of the browser's time zone.
const formatters = new Map<string, Intl.DateTimeFormat>();

function parts(d: Date, tz: string): Record<string, string> {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
    formatters.set(tz, f);
  }
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(d)) out[p.type] = p.value;
  return out;
}

export const localDate = (d: Date | string, tz: string): string => { const p = parts(new Date(d), tz); return `${p.year}-${p.month}-${p.day}`; };
export const localTime = (d: Date | string, tz: string): string => { const p = parts(new Date(d), tz); return `${p.hour}:${p.minute}`; };
export const today = (tz: string): string => localDate(new Date(), tz);

export function zonedToUtc(date: string, time: string, tz: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const wall = Date.UTC(y, m - 1, d, hh, mm);
  const offset = (t: number): number => { const p = parts(new Date(t), tz); return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - t; };
  let ts = wall - offset(wall);
  const second = offset(ts);
  if (second !== wall - ts) ts = wall - second;
  return new Date(ts);
}

export const addDays = (date: string, n: number): string => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
export const weekday = (date: string): number => new Date(`${date}T12:00:00Z`).getUTCDay();
export const startOfWeek = (date: string, weekStartsOn = 1): string => addDays(date, -((weekday(date) - weekStartsOn + 7) % 7));
export const firstOfMonth = (date: string): string => `${date.slice(0, 8)}01`;
export const shiftMonth = (date: string, n: number): string => {
  const [y, m] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10);
};
export const monthGrid = (date: string, weekStartsOn = 1): string[] => {
  const start = startOfWeek(firstOfMonth(date), weekStartsOn);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};
export const minutesOfDay = (iso: string, tz: string): number => {
  const [h, m] = localTime(iso, tz).split(':').map(Number);
  return h * 60 + m;
};
export const durationMinutes = (a: string, b: string): number => Math.round((Date.parse(b) - Date.parse(a)) / 60_000);
/** Range [from, to) in UTC ISO strings for local dates. */
export const dayRange = (from: string, toExclusive: string, tz: string): { from: string; to: string } => ({
  from: zonedToUtc(from, '00:00', tz).toISOString(), to: zonedToUtc(toExclusive, '00:00', tz).toISOString(),
});
/** Value for <input type="datetime-local"> in the gym's time zone. */
export const toLocalInput = (iso: string | null | undefined, tz: string): string => (iso ? `${localDate(iso, tz)}T${localTime(iso, tz)}` : '');
export const fromLocalInput = (value: string, tz: string): string | null => {
  if (!value) return null;
  const [d, t] = value.split('T');
  return zonedToUtc(d, t.slice(0, 5), tz).toISOString();
};
